import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GiftIcon, TagIcon, WrenchIcon, ShirtIcon, CakeIcon, CheckCircleIcon, ArrowRightIcon, WalletIcon, SparklesIcon, MinusIcon, PlusIcon, AlertCircleIcon, ZapIcon, StarIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../../lib/AuthContext';
import { MOCK_REWARDS, MOCK_POINTS_SETTINGS } from '../../lib/mockData';
import { formatVND } from '../../lib/formatters';
import type { Reward } from '../../lib/types';
const QUICK_CONVERT_OPTIONS = [200, 500, 1000, 2000];
const POINTS_PER_UNIT = MOCK_POINTS_SETTINGS.pointsPerRedemptionUnit; // 1000
const VND_PER_UNIT = MOCK_POINTS_SETTINGS.redemptionUnitValueVnd; // 500,000
function pointsToVnd(points: number): number {
  return Math.floor(points / POINTS_PER_UNIT) * VND_PER_UNIT;
}
export function RewardsPage() {
  const {
    currentCustomer,
    isCustomerLoggedIn,
    updateCustomer
  } = useAuth();
  // Quick convert state
  const [convertPoints, setConvertPoints] = useState(0);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [convertProcessing, setConvertProcessing] = useState(false);
  // Reward redemption state
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [redeemProcessing, setRedeemProcessing] = useState(false);
  // Success toast
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3500);
  }, []);
  if (!isCustomerLoggedIn || !currentCustomer) {
    return <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-id="element-1607">
        <GiftIcon className="w-16 h-16 text-[#666666] mx-auto mb-4" data-id="element-1608" />
        <h2 className="text-xl font-semibold text-white mb-2" data-id="element-1609">
          Vui lòng đăng nhập
        </h2>
        <p className="text-[#B0B0B0]" data-id="element-1610">
          Đăng nhập để đổi điểm và nhận phần thưởng
        </p>
      </div>;
  }
  const userPoints = currentCustomer.points;
  const convertCredit = pointsToVnd(convertPoints);
  const canConvert = convertPoints > 0 && convertPoints <= userPoints && convertCredit > 0;
  // Quick convert handlers
  const handleConvertSelect = (pts: number) => {
    setConvertPoints(pts);
  };
  const adjustConvertPoints = (delta: number) => {
    setConvertPoints(prev => {
      const next = prev + delta;
      if (next < 0) return 0;
      return next;
    });
  };
  const handleConvertConfirm = () => {
    if (!canConvert) return;
    setConvertProcessing(true);
    setTimeout(() => {
      updateCustomer({
        points: currentCustomer.points - convertPoints,
        walletBalance: currentCustomer.walletBalance + convertCredit
      });
      setConvertProcessing(false);
      setShowConvertConfirm(false);
      setConvertPoints(0);
      showToast(`Đã đổi ${convertPoints.toLocaleString()} điểm → ${formatVND(convertCredit)} tín dụng!`);
    }, 1200);
  };
  // Reward redemption handlers
  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'voucher':
        return <TagIcon className="w-6 h-6" data-id="element-1611" />;
      case 'service':
        return <WrenchIcon className="w-6 h-6" data-id="element-1612" />;
      case 'merchandise':
        return <ShirtIcon className="w-6 h-6" data-id="element-1613" />;
      case 'birthday':
        return <CakeIcon className="w-6 h-6" data-id="element-1614" />;
      default:
        return <GiftIcon className="w-6 h-6" data-id="element-1615" />;
    }
  };
  const handleRedeemConfirm = () => {
    if (!selectedReward) return;
    const canAfford = currentCustomer.points >= selectedReward.pointsCost;
    if (!canAfford) {
      showToast('Bạn không đủ điểm để đổi phần thưởng này', 'error');
      setSelectedReward(null);
      return;
    }
    setRedeemProcessing(true);
    setTimeout(() => {
      const isVoucher = selectedReward.category === 'voucher';
      const creditValue = isVoucher ? pointsToVnd(selectedReward.pointsCost) : 0;
      updateCustomer({
        points: currentCustomer.points - selectedReward.pointsCost,
        ...(creditValue > 0 ? {
          walletBalance: currentCustomer.walletBalance + creditValue
        } : {})
      });
      setRedeemProcessing(false);
      setSelectedReward(null);
      if (isVoucher) {
        showToast(`Đã đổi ${selectedReward.nameVi}! +${formatVND(creditValue)} vào ví`);
      } else {
        showToast(`Đã đổi ${selectedReward.nameVi}! Vui lòng nhận tại cửa hàng`);
      }
    }, 1200);
  };
  const availableRewards = MOCK_REWARDS.filter(r => r.isAvailable && r.pointsCost > 0);
  return <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-1616">
      {/* Toast */}
      <AnimatePresence data-id="element-1617">
        {toast && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium ${toast.type === 'success' ? 'bg-[#22C55E] text-white' : 'bg-[#EF4444] text-white'}`} data-id="element-1618">
            <CheckCircleIcon className="w-5 h-5" data-id="element-1619" />
            {toast.message}
          </motion.div>}
      </AnimatePresence>

      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1620">
        <div className="flex items-center justify-between mb-6" data-id="element-1621">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-1622">
            <GiftIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1623" />
            Đổi Điểm & Phần Thưởng
          </h1>
          <Badge variant="gold" size="md" data-id="element-1624">
            {userPoints.toLocaleString('vi-VN')} điểm
          </Badge>
        </div>
      </motion.div>

      {/* ===== QUICK CONVERT SECTION ===== */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.05
    }} className="mb-8" data-id="element-1625">
        <div className="bg-gradient-to-br from-[#C8A84E]/10 to-[#C8A84E]/5 border border-[#C8A84E]/25 rounded-xl p-5 md:p-6" data-id="element-1626">
          <div className="flex items-center gap-2 mb-1" data-id="element-1627">
            <ZapIcon className="w-5 h-5 text-[#C8A84E]" data-id="element-1628" />
            <h2 className="text-lg font-bold text-white" data-id="element-1629">
              Đổi Điểm → Tín Dụng Mua Hàng
            </h2>
          </div>
          <p className="text-sm text-[#B0B0B0] mb-5" data-id="element-1630">
            Quy đổi nhanh:{' '}
            <span className="text-[#C8A84E] font-semibold" data-id="element-1631">
              {POINTS_PER_UNIT.toLocaleString()} điểm ={' '}
              {formatVND(VND_PER_UNIT)}
            </span>{' '}
            tín dụng vào ví
          </p>

          {/* Quick select buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-id="element-1632">
            {QUICK_CONVERT_OPTIONS.map(pts => {
            const credit = pointsToVnd(pts);
            const affordable = userPoints >= pts;
            return <button key={pts} onClick={() => affordable && handleConvertSelect(pts)} disabled={!affordable} className={`relative py-3 px-3 rounded-lg border text-center transition-all duration-200 ${convertPoints === pts ? 'border-[#C8A84E] bg-[#C8A84E]/15 ring-1 ring-[#C8A84E]/30' : affordable ? 'border-[#2A2A2A] hover:border-[#444]' : 'border-[#2A2A2A] opacity-40 cursor-not-allowed'}`} data-id="element-1633">
                  <p className={`text-base font-bold ${convertPoints === pts ? 'text-[#C8A84E]' : 'text-white'}`} data-id="element-1634">
                    {pts.toLocaleString()} điểm
                  </p>
                  <p className="text-xs text-[#22C55E] mt-0.5" data-id="element-1635">
                    → {formatVND(credit)}
                  </p>
                </button>;
          })}
          </div>

          {/* Custom amount stepper */}
          <div className="flex items-center gap-3 mb-4" data-id="element-1636">
            <span className="text-sm text-[#B0B0B0] whitespace-nowrap" data-id="element-1637">
              Tùy chỉnh:
            </span>
            <div className="flex items-center gap-2 bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-2 py-1.5" data-id="element-1638">
              <button onClick={() => adjustConvertPoints(-100)} disabled={convertPoints < 100} className="w-8 h-8 rounded-md bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center text-[#B0B0B0] hover:text-white hover:border-[#444] disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-1639">
                <MinusIcon className="w-4 h-4" data-id="element-1640" />
              </button>
              <input type="text" value={convertPoints || ''} onChange={e => {
              const val = parseInt(e.target.value.replace(/\D/g, ''), 10);
              setConvertPoints(isNaN(val) ? 0 : val);
            }} placeholder="0" className="w-24 text-center bg-transparent text-white font-bold text-lg focus:outline-none" data-id="element-1641" />
              <button onClick={() => adjustConvertPoints(100)} disabled={convertPoints + 100 > userPoints} className="w-8 h-8 rounded-md bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center text-[#B0B0B0] hover:text-white hover:border-[#444] disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-1642">
                <PlusIcon className="w-4 h-4" data-id="element-1643" />
              </button>
            </div>
            <span className="text-xs text-[#666]" data-id="element-1644">điểm</span>
            {convertPoints > userPoints && <span className="text-xs text-[#EF4444]" data-id="element-1645">
                Vượt quá số điểm hiện có
              </span>}
          </div>

          {/* Convert preview */}
          <AnimatePresence data-id="element-1646">
            {convertPoints > 0 && convertCredit > 0 && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-1647">
                <div className="bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg p-4 mb-4" data-id="element-1648">
                  <div className="flex items-center justify-between mb-2" data-id="element-1649">
                    <span className="text-sm text-[#B0B0B0]" data-id="element-1650">Điểm đổi</span>
                    <span className="text-sm text-white font-medium" data-id="element-1651">
                      -{convertPoints.toLocaleString()} điểm
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2" data-id="element-1652">
                    <span className="text-sm text-[#B0B0B0]" data-id="element-1653">
                      Tín dụng nhận
                    </span>
                    <span className="text-sm text-[#22C55E] font-semibold" data-id="element-1654">
                      +{formatVND(convertCredit)}
                    </span>
                  </div>
                  <div className="border-t border-[#2A2A2A] pt-2 mt-2 flex items-center justify-between" data-id="element-1655">
                    <span className="text-sm text-[#B0B0B0]" data-id="element-1656">Điểm còn lại</span>
                    <span className={`text-sm font-bold ${userPoints - convertPoints >= 0 ? 'text-white' : 'text-[#EF4444]'}`} data-id="element-1657">
                      {(userPoints - convertPoints).toLocaleString('vi-VN')}{' '}
                      điểm
                    </span>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          <Button fullWidth onClick={() => setShowConvertConfirm(true)} disabled={!canConvert} data-id="element-1658">
            <WalletIcon className="w-4 h-4" data-id="element-1659" />
            Đổi{' '}
            {convertPoints > 0 ? `${convertPoints.toLocaleString()} Điểm` : 'Điểm'}{' '}
            → Tín Dụng
            {convertCredit > 0 && <span className="ml-1 text-[#22C55E]" data-id="element-1660">
                ({formatVND(convertCredit)})
              </span>}
          </Button>
        </div>
      </motion.div>

      {/* ===== REWARD CATALOG ===== */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="mb-4" data-id="element-1661">
        <div className="flex items-center gap-2 mb-4" data-id="element-1662">
          <SparklesIcon className="w-5 h-5 text-[#C8A84E]" data-id="element-1663" />
          <h2 className="text-lg font-bold text-white" data-id="element-1664">Phần Thưởng Khác</h2>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1665">
        {availableRewards.map((reward, i) => {
        const canAfford = userPoints >= reward.pointsCost;
        const isVoucher = reward.category === 'voucher';
        return <motion.div key={reward.id} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.12 + i * 0.04
        }} data-id="element-1666">
              <Card className={`h-full flex flex-col ${!canAfford ? 'opacity-50' : ''}`} data-id="element-1667">
                <div className="flex items-start justify-between mb-3" data-id="element-1668">
                  <div className="w-12 h-12 rounded-xl bg-[#C8A84E]/10 flex items-center justify-center text-[#C8A84E]" data-id="element-1669">
                    {getCategoryIcon(reward.category)}
                  </div>
                  {isVoucher && <Badge variant="success" size="sm" data-id="element-1670">
                      Cộng ví
                    </Badge>}
                </div>
                <h3 className="text-base font-semibold text-white mb-1" data-id="element-1671">
                  {reward.nameVi}
                </h3>
                <p className="text-sm text-[#B0B0B0] mb-4 flex-1" data-id="element-1672">
                  {reward.description}
                </p>
                <div className="flex items-center justify-between" data-id="element-1673">
                  <div data-id="element-1674">
                    <span className="text-lg font-bold text-[#C8A84E]" data-id="element-1675">
                      {reward.pointsCost.toLocaleString()}
                    </span>
                    <span className="text-sm text-[#C8A84E] ml-1" data-id="element-1676">điểm</span>
                  </div>
                  <Button size="sm" variant={canAfford ? 'primary' : 'ghost'} disabled={!canAfford} onClick={() => setSelectedReward(reward)} data-id="element-1677">
                    {canAfford ? 'Đổi Ngay' : 'Chưa đủ'}
                  </Button>
                </div>
                {reward.stock <= 10 && <p className="text-xs text-[#F59E0B] mt-2 flex items-center gap-1" data-id="element-1678">
                    <AlertCircleIcon className="w-3 h-3" data-id="element-1679" />
                    Còn {reward.stock} phần
                  </p>}
              </Card>
            </motion.div>;
      })}
      </div>

      {/* Conversion info */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.3
    }} className="mt-8 text-center" data-id="element-1680">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full" data-id="element-1681">
          <StarIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-1682" />
          <span className="text-xs text-[#B0B0B0]" data-id="element-1683">
            Tỷ giá:{' '}
            <span className="text-white font-semibold" data-id="element-1684">
              {POINTS_PER_UNIT.toLocaleString()} điểm
            </span>{' '}
            ={' '}
            <span className="text-[#22C55E] font-semibold" data-id="element-1685">
              {formatVND(VND_PER_UNIT)}
            </span>{' '}
            tín dụng
          </span>
        </div>
      </motion.div>

      {/* ===== CONVERT CONFIRM MODAL ===== */}
      <Modal isOpen={showConvertConfirm} onClose={() => setShowConvertConfirm(false)} title="Xác Nhận Đổi Điểm" data-id="element-1686">
        <div className="space-y-4" data-id="element-1687">
          <div className="bg-[#141414] rounded-lg p-4 space-y-3" data-id="element-1688">
            <div className="flex items-center justify-center gap-3 py-2" data-id="element-1689">
              <div className="text-center" data-id="element-1690">
                <p className="text-2xl font-bold text-white" data-id="element-1691">
                  {convertPoints.toLocaleString()}
                </p>
                <p className="text-xs text-[#666]" data-id="element-1692">điểm</p>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-[#C8A84E]" data-id="element-1693" />
              <div className="text-center" data-id="element-1694">
                <p className="text-2xl font-bold text-[#22C55E]" data-id="element-1695">
                  {formatVND(convertCredit)}
                </p>
                <p className="text-xs text-[#666]" data-id="element-1696">tín dụng</p>
              </div>
            </div>
            <div className="border-t border-[#2A2A2A] pt-3 space-y-2" data-id="element-1697">
              <div className="flex justify-between text-sm" data-id="element-1698">
                <span className="text-[#B0B0B0]" data-id="element-1699">Điểm hiện tại</span>
                <span className="text-white" data-id="element-1700">
                  {userPoints.toLocaleString('vi-VN')} điểm
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1701">
                <span className="text-[#B0B0B0]" data-id="element-1702">Điểm sau đổi</span>
                <span className="text-white font-semibold" data-id="element-1703">
                  {(userPoints - convertPoints).toLocaleString('vi-VN')} điểm
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1704">
                <span className="text-[#B0B0B0]" data-id="element-1705">Tín dụng cộng vào ví</span>
                <span className="text-[#22C55E] font-semibold" data-id="element-1706">
                  +{formatVND(convertCredit)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2 text-xs text-[#666666]" data-id="element-1707">
            <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-1708" />
            <span data-id="element-1709">
              Tín dụng sẽ được cộng ngay vào ví. Giao dịch không thể hoàn tác.
            </span>
          </div>
          <div className="flex gap-3" data-id="element-1710">
            <Button variant="ghost" fullWidth onClick={() => setShowConvertConfirm(false)} data-id="element-1711">
              Hủy
            </Button>
            <Button fullWidth onClick={handleConvertConfirm} isLoading={convertProcessing} data-id="element-1712">
              Xác Nhận Đổi
            </Button>
          </div>
        </div>
      </Modal>

      {/* ===== REWARD REDEEM MODAL ===== */}
      <Modal isOpen={!!selectedReward} onClose={() => setSelectedReward(null)} title="Xác Nhận Đổi Thưởng" data-id="element-1713">
        {selectedReward && <div className="space-y-4" data-id="element-1714">
            <div className="bg-[#141414] rounded-lg p-4" data-id="element-1715">
              <div className="flex items-center gap-3 mb-2" data-id="element-1716">
                <div className="w-10 h-10 rounded-lg bg-[#C8A84E]/10 flex items-center justify-center text-[#C8A84E]" data-id="element-1717">
                  {getCategoryIcon(selectedReward.category)}
                </div>
                <div data-id="element-1718">
                  <h3 className="font-semibold text-white" data-id="element-1719">
                    {selectedReward.nameVi}
                  </h3>
                  <p className="text-xs text-[#B0B0B0]" data-id="element-1720">
                    {selectedReward.description}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-2" data-id="element-1721">
              <div className="flex justify-between text-sm" data-id="element-1722">
                <span className="text-[#B0B0B0]" data-id="element-1723">Chi phí</span>
                <span className="text-[#C8A84E] font-semibold" data-id="element-1724">
                  {selectedReward.pointsCost.toLocaleString()} điểm
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1725">
                <span className="text-[#B0B0B0]" data-id="element-1726">Điểm hiện tại</span>
                <span className="text-white" data-id="element-1727">
                  {userPoints.toLocaleString('vi-VN')} điểm
                </span>
              </div>
              <div className="flex justify-between text-sm border-t border-[#2A2A2A] pt-2" data-id="element-1728">
                <span className="text-[#B0B0B0]" data-id="element-1729">Điểm còn lại</span>
                <span className="text-white font-semibold" data-id="element-1730">
                  {(userPoints - selectedReward.pointsCost).toLocaleString('vi-VN')}{' '}
                  điểm
                </span>
              </div>
              {selectedReward.category === 'voucher' && <div className="flex justify-between text-sm bg-[#22C55E]/5 border border-[#22C55E]/20 rounded-lg px-3 py-2 mt-2" data-id="element-1731">
                  <span className="text-[#22C55E]" data-id="element-1732">Cộng vào ví</span>
                  <span className="text-[#22C55E] font-bold" data-id="element-1733">
                    +{formatVND(pointsToVnd(selectedReward.pointsCost))}
                  </span>
                </div>}
              {selectedReward.category !== 'voucher' && <div className="flex items-start gap-2 text-xs text-[#F59E0B] bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-lg px-3 py-2 mt-2" data-id="element-1734">
                  <GiftIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-1735" />
                  <span data-id="element-1736">Phần thưởng sẽ được nhận tại cửa hàng Mỹ Hòa Phát</span>
                </div>}
            </div>
            <div className="flex gap-3" data-id="element-1737">
              <Button variant="ghost" fullWidth onClick={() => setSelectedReward(null)} data-id="element-1738">
                Hủy
              </Button>
              <Button fullWidth onClick={handleRedeemConfirm} isLoading={redeemProcessing} data-id="element-1739">
                Xác Nhận Đổi
              </Button>
            </div>
          </div>}
      </Modal>
    </div>;
}